import {
  IAdvancedSearchCriterion,
  IWorkPhase
} from '@publishing-engine/domain/models';
import { Location } from 'react-router-dom';
import { IListTemplatesQuery } from 'src/api/email-service';
import {
  IMyReviewsFilters,
  IMySubmissionFilter,
  ISubmissionSortOption,
  IWorkspaceSubmissionFilter
} from 'src/api/queries';
import { SortField } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';

/**
 * Advanced-search criteria are an array of `{ field, value }` objects, so they
 * cannot use the comma-joined encoding the other array filters use. They travel
 * as a single JSON-encoded param instead.
 */
export const ADVANCED_SEARCH_PARAM = 'advancedSearch';

/**
 * A criterion's value is an array for fields that accept several values and a
 * plain string otherwise. Both shapes reach the UI (from the URL, a saved
 * filter, or the panel), so anything rendering one goes through here.
 */
export const formatCriterionValue = (
  value: IAdvancedSearchCriterion['value']
): string => {
  if (Array.isArray(value)) {
    return value.filter(item => item.trim()).join(', ');
  }

  return value ?? '';
};

/**
 * Reads the advanced-search criteria out of the URL. Deliberately defensive: a
 * hand-edited or truncated param degrades to "no criteria" instead of breaking
 * the page, and any entry that isn't a `{ field, value }` pair is dropped.
 */
export const parseAdvancedSearchParam = (
  queryParams: URLSearchParams
): IAdvancedSearchCriterion[] => {
  const raw = queryParams.get(ADVANCED_SEARCH_PARAM);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (item): item is IAdvancedSearchCriterion =>
        !!item &&
        typeof item.field === 'string' &&
        (typeof item.value === 'string' ||
          // Multi-value criteria carry an array of strings. Anything else
          // (numbers, nested objects, a mixed array) is malformed and dropped.
          (Array.isArray(item.value) &&
            item.value.every((value: unknown) => typeof value === 'string')))
    );
  } catch {
    return [];
  }
};

/**
 * List context carried in router state from the workspace submissions list into
 * the submission details page, so "Next" can ask the backend for the following
 * submission. The backend resolves "next" by index within the filtered list, so
 * `index` and `filters` must describe exactly the list the user was looking at.
 */
export type SubmissionNavContext = {
  /** Absolute global index of the current submission within the filtered list. */
  index: number;
  /**
   * Size of the filtered list the user came from. "Next" is disabled on the last
   * item, which is also where the backend wraps `nextGlobalIndex` back to 0.
   */
  total?: number;
  offset: number;
  limit: number;
  workspaceId: string;
  /** Effective filters as sent to the backend, not the raw URL params. */
  filters: IWorkspaceSubmissionFilter;
  sort: ISubmissionSortOption | null;
};

export const parseQueryParams = (
  search: string
): {
  filters: IWorkspaceSubmissionFilter;
  sort: SortField | null;
} => {
  const queryParams = new URLSearchParams(search);

  // Known keys
  const baseFilters: IWorkspaceSubmissionFilter = {
    workflowPhase: (queryParams.get('workflowPhase')?.split(',') ||
      []) as IWorkPhase[],
    journalId: queryParams.get('journalId')?.split(',') || [],
    documentType: queryParams.get('documentType')?.split(',') || [],
    assigneeId: queryParams.get('assigneeId')?.split(',') || [],
    tagId: queryParams.get('tagId')?.split(',') || [],
    searchParams: queryParams.get('searchParams') || '',
    focusIssueId: queryParams.get('focusIssueId')?.split(',') || [],
    userTags: queryParams.get('userTags')?.split(',') || [],
    expertPhases: queryParams.get('expertPhases')?.split(',') || []
  };

  // Add any extra keys dynamically. These are dashboard-defined filter keys.
  // Skip known UI params that are not filters so they don't leak into the
  // filter payload (e.g. `activeTab` for tabs, `sort` handled separately), and
  // skip advanced search, which needs its own decoding below.
  const NON_FILTER_PARAMS = new Set([
    'activeTab',
    'sort',
    'selectedItem',
    ADVANCED_SEARCH_PARAM
  ]);
  queryParams.forEach((value, key) => {
    if (!(key in baseFilters) && !NON_FILTER_PARAMS.has(key)) {
      baseFilters[key] = value.includes(',') ? value.split(',') : value;
    }
  });

  // Only set when the URL actually carries criteria, so pages that never use
  // advanced search don't gain an extra key on their filter object.
  if (queryParams.has(ADVANCED_SEARCH_PARAM)) {
    baseFilters.advancedSearch = parseAdvancedSearchParam(queryParams);
  }

  return {
    filters: baseFilters,
    sort: parseSortFromQuery(queryParams)
  };
};

export const updateQueryParams = (
  filters: Record<string, unknown>,
  location: Omit<Location, 'state' | 'key'>,
  navigate: (path: string, options?: any) => void
) => {
  const queryParams = new URLSearchParams(location.search);
  Object.entries(filters).forEach(([key, value]) => {
    if (key === ADVANCED_SEARCH_PARAM) {
      if (Array.isArray(value) && value.length > 0) {
        queryParams.set(key, JSON.stringify(value));
      } else {
        queryParams.delete(key);
      }
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      queryParams.set(key, value.join(','));
    } else if (typeof value === 'string' && value.trim() !== '') {
      queryParams.set(key, value);
    } else {
      // Remove key if no value
      queryParams.delete(key);
    }
  });

  if (
    queryParams.toString() !== new URLSearchParams(location.search).toString()
  ) {
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
  }
};

export const parseSubmissionQueryParams = (
  search: string
): {
  filters: IMySubmissionFilter;
  sort: SortField | null;
} => {
  const queryParams = new URLSearchParams(search);

  return {
    filters: {
      workflowPhase: (queryParams.get('workflowPhase')?.split(',') ||
        []) as IWorkPhase[],
      journalId: queryParams.get('journalId')?.split(',') || [],
      searchParams: queryParams.get('searchParams') || '',
      submissionType: (() => {
        const value = queryParams.get('submissionType');
        return value === 'my-submissions-active' ||
          value === 'my-submissions-draft' ||
          value === 'my-submissions-revision' ||
          value === 'my-submissions-unsubmitted' ||
          value === 'my-submissions-completed'
          ? value
          : undefined;
      })()
    },
    sort: parseSortFromQuery(queryParams)
  };
};

export const parseReviewQueryParams = (
  search: string
): {
  filters: IMyReviewsFilters;
  sort: SortField | null;
} => {
  const queryParams = new URLSearchParams(search);

  return {
    filters: {
      journalId: queryParams.get('journalId')?.split(',') || [],
      searchParams: queryParams.get('searchParams') || '',
      reviewListType: (() => {
        const value = queryParams.get('reviewListType');
        return value === 'active' || value === 'completed' ? value : undefined;
      })()
    },
    sort: parseSortFromQuery(queryParams)
  };
};

export const parseEmailQueryParams = (search: string): IListTemplatesQuery => {
  const queryParams = new URLSearchParams(search);
  return {
    event: queryParams.get('event')?.split(',') || [],
    scope: queryParams.get('scope')?.split(',') || [],
    category: queryParams.get('category')?.split(',') || [],
    search: queryParams.get('search') || '',
    active: queryParams.get('active')?.split(',') || [],
    phases: queryParams.get('phases')?.split(',') || [],
    submissionContextId:
      queryParams.get('submissionContextId')?.split(',') || []
  };
};

export const parseSortFromQuery = (
  queryParams: URLSearchParams
): SortField | null => {
  const sortParam = queryParams.get('sort');

  if (!sortParam) return null;

  const [label, value, direction] = sortParam.split(',');

  if (label && value && (direction === 'asc' || direction === 'desc')) {
    return {
      label,
      value,
      direction
    };
  }

  return null;
};
